/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Modal, Input, Radio, Empty, Space } from 'antd';
import { useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';

export interface ImportToProjectBuilderProps {
  visible: boolean;
  onClose: () => void;
  onImport: (movingFolderId: number, targetFolderId: number) => void;
  targetFolder: { id: number; name: string; parent_id: number | null } | null;
  allFolders: any[];
  newFolders: any[];
}

export const ImportToProjectBuilder: React.FC<ImportToProjectBuilderProps> = ({
  visible,
  onClose,
  onImport,
  targetFolder,
  allFolders,
  newFolders,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);

  // Helper to check if a folder is a descendant of the target folder
  const isDescendant = (
    targetId: number,
    potentialDescendantId: number
  ): boolean => {
    if (targetId === potentialDescendantId) return true;

    const folder = allFolders.find((f) => f.id === potentialDescendantId);
    if (!folder || !folder.parent_id) return false;

    return isDescendant(targetId, folder.parent_id);
  };

  // Helper to check if a folder (or any of its descendants) has new folders
  const hasNewFolderDescendants = (folderId: number): boolean => {
    // Check if folder itself is new
    const folder = allFolders.find((f) => f.id === folderId);
    if (folder?.isNew) return true;

    // Check if any children are new or have new descendants
    const children = allFolders.filter((f) => f.parent_id === folderId);
    return children.some((child) => hasNewFolderDescendants(child.id));
  };

  // Filter and prepare folders for display
  const availableFolders = useMemo(() => {
    if (!targetFolder) return [];

    // Build tree with ALL folders (both new and existing)
    // This ensures we see the full hierarchy
    const buildTree = (parentId: number | null, level = 0): any[] => {
      return allFolders
        .filter((f) => f.parent_id === parentId)
        .filter(
          (f) =>
            f.id !== targetFolder.id && !isDescendant(targetFolder.id, f.id)
        )
        .filter((f) => {
          // Include new folders always
          if (f.isNew) return true;
          // Include existing folders only if they have new folder descendants
          return hasNewFolderDescendants(f.id);
        })
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((f) => ({
          ...f,
          level,
          children: buildTree(f.id, level + 1),
        }));
    };

    const fullTree = buildTree(null);

    // If no search query, flatten and return all
    if (!searchQuery) {
      const flattenTree = (nodes: any[]): any[] => {
        return nodes.flatMap((node) => [
          { ...node, children: undefined },
          ...flattenTree(node.children || []),
        ]);
      };
      return flattenTree(fullTree);
    }

    // Tree-aware search: show folder if it OR any descendant matches
    const searchLower = searchQuery.toLowerCase();

    const filterTree = (nodes: any[]): any[] => {
      return nodes
        .map((node) => {
          const nameMatches = node.name.toLowerCase().includes(searchLower);
          const filteredChildren = filterTree(node.children || []);
          const hasMatchingChildren = filteredChildren.length > 0;

          // Include node if it matches OR has matching children
          if (nameMatches || hasMatchingChildren) {
            return {
              ...node,
              children: filteredChildren,
            };
          }
          return null;
        })
        .filter((node): node is NonNullable<typeof node> => node !== null);
    };

    const filteredTree = filterTree(fullTree);

    // Flatten filtered tree for display
    const flattenTree = (nodes: any[]): any[] => {
      return nodes.flatMap((node) => [
        { ...node, children: undefined },
        ...flattenTree(node.children || []),
      ]);
    };

    return flattenTree(filteredTree);
  }, [targetFolder, allFolders, searchQuery, isDescendant]);

  const handleOk = () => {
    if (selectedFolderId !== null && targetFolder) {
      onImport(selectedFolderId, targetFolder.id);
      setSearchQuery('');
      setSelectedFolderId(null);
    }
  };

  const handleCancel = () => {
    setSearchQuery('');
    setSelectedFolderId(null);
    onClose();
  };

  // Reset selected folder when modal opens
  useMemo(() => {
    if (visible) {
      setSelectedFolderId(null);
    }
  }, [visible]);

  return (
    <Modal
      title={
        <ModalTitle
          text={
            targetFolder
              ? `${intl.formatMessage({ id: 'grap-folders-to' })} "${
                  targetFolder.name
                }"`
              : intl.formatMessage({ id: 'grap' })
          }
          icon={['far', 'arrow-alt-to-right']}
        />
      }
      visible={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={intl.formatMessage({ id: 'grap-selected-folder' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      okButtonProps={{ disabled: selectedFolderId === null }}
      centered
      width={600}>
      <div css={styles.container}>
        <Input
          placeholder={intl.formatMessage({
            id: 'move-folder-search-placeholder',
          })}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          prefix={<FontAwesomeIcon icon={['far', 'search']} />}
          css={styles.searchInput}
        />

        <div css={styles.label}>
          {intl.formatMessage({ id: 'select-folder-to-grap' })}
        </div>

        <div
          css={styles.folderList}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleOk();
            }
          }}>
          <Radio.Group
            value={selectedFolderId}
            onChange={(e) => setSelectedFolderId(e.target.value)}
            css={styles.radioGroup}>
            <Space direction="vertical" css={styles.spaceContainer}>
              {/* Folder list */}
              {availableFolders.length > 0 ? (
                availableFolders.map((f) => {
                  // Only show radio button for NEW folders
                  if (f.isNew) {
                    return (
                      <Radio key={f.id} value={f.id} css={styles.radioItem}>
                        <div
                          css={styles.folderItem}
                          style={{ marginLeft: `${f.level * 24}px` }}>
                          <FontAwesomeIcon
                            icon={['far', 'folder']}
                            css={styles.newFolderIcon}
                          />
                          <span css={styles.folderName}>{f.name}</span>
                        </div>
                      </Radio>
                    );
                  } else {
                    // Existing folder - show without radio button (for context)
                    return (
                      <div
                        key={f.id}
                        css={styles.existingFolderWrapper}
                        style={{ marginLeft: `${f.level * 24 + 32}px` }}>
                        <div css={styles.folderItem}>
                          <FontAwesomeIcon
                            icon={['fas', 'folder']}
                            css={styles.existingFolderIcon}
                          />
                          <span
                            css={[
                              styles.folderName,
                              styles.existingFolderText,
                            ]}>
                            {f.name}
                          </span>
                        </div>
                      </div>
                    );
                  }
                })
              ) : (
                <Empty
                  description={intl.formatMessage({ id: 'no-folders-yet' })}
                  css={styles.empty}
                />
              )}
            </Space>
          </Radio.Group>
        </div>
      </div>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  searchInput: {
    marginBottom: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: theme.colors.text.primary,
    marginBottom: '8px',
  },
  folderList: {
    maxHeight: '400px',
    overflowY: 'auto',
    border: `1px solid ${theme.colors.lightGray}`,
    borderRadius: '4px',
    padding: '12px',
  },
  radioGroup: {
    width: '100%',
  },
  spaceContainer: {
    width: '100%',
  },
  radioItem: {
    width: '100%',
    marginBottom: '4px',
    '&:hover': {
      backgroundColor: theme.colors.primaryLight,
    },
  },
  folderItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px',
  },
  folderIcon: {
    fontSize: '16px',
    flexShrink: 0,
  },
  newFolderIcon: {
    fontSize: '16px',
    flexShrink: 0,
    color: '#52c41a',
  },
  folderName: {
    fontSize: '14px',
    color: theme.colors.text.primary,
  },
  empty: {
    padding: '24px',
  },
  existingFolderWrapper: {
    width: '100%',
    marginBottom: '4px',
    opacity: 0.6,
  },
  existingFolderIcon: {
    fontSize: '16px',
    flexShrink: 0,
    color: '#faad14',
    opacity: 0.5,
  },
  existingFolderText: {
    fontStyle: 'italic',
    color: theme.colors.textSecondary,
  },
});
